// crowdsource-fields.js — single source of truth for the report/apply
// field whitelist.
//
// This file is loaded early (before screens.jsx) so window.REPORT_FIELD_GROUPS
// is available to the ReportCourseSheet picker.
//
// CANONICAL SPEC. Two consumers must stay in sync with this file:
//
//   1. Frontend picker — screens.jsx ReportCourseSheet reads
//      window.REPORT_FIELD_GROUPS and renders the <select> from it.
//
//   2. Backend whitelist — supabase-migration-crowdsource-rpc.sql
//      apply_report() PL/pgSQL function has a CASE statement that MUST
//      enumerate exactly the same `key` values, with the right Postgres
//      type cast for each. Adding a row here without adding the matching
//      SQL branch means the reporter submits but apply silently no-ops.
//
// When adding a field:
//   a. Add { key, en, zh } here under the right group.
//   b. Add the matching `when 'key' then update ...` branch in BOTH the
//      "null/empty" CASE and the "with value" CASE in apply_report().
//   c. Bump CROWDSOURCE_FIELDS_VERSION below (any number — used only to
//      help future migrations detect drift).
//   d. Run the SQL migration, redeploy apply-report Edge Function (no
//      changes), redeploy client.
//
// Removing a field is trickier — existing course_reports rows may have
// it as field_path. Leave the SQL branch in place even if you hide the
// field from the picker, so older accepted reports can still be applied.

(function () {
  'use strict';

  // Bumped to 4: `time_text` moved from a free-text box to input kind
  // 'timerange' — a 4-wheel start→end picker that can only produce the
  // canonical "HH:MM-HH:MM". SQL-neutral: apply_report still casts it as text,
  // so no migration rides with this bump.
  //   v3: added the `sessions_list` schedule field (input kind
  // 'schedule') — a canonical per-session list edited via SessionScheduleDialog.
  // Its suggested_value is a JSON array string; apply_report casts it ::jsonb,
  // derives start_date/end_date/sessions from it, and locks all four columns.
  //   v2: each item carries an `input` descriptor consumed by both the reporter
  //   picker and the admin apply dialog so date / number / enum fields stop
  //   accepting raw strings (which the SQL ::date / ::numeric casts then reject).
  // Keep this aligned with the type casts inside apply_report() in
  // supabase-migration-crowdsource-rpc.sql.
  const CROWDSOURCE_FIELDS_VERSION = 4;

  // Input descriptors:
  //   { kind: 'text' }       — plain string
  //   { kind: 'number', step?, min? } — numeric (::numeric in SQL)
  //   { kind: 'integer', min? }       — whole number (::integer in SQL)
  //   { kind: 'date' }       — YYYY-MM-DD (::date in SQL)
  //   { kind: 'timerange' }  — "HH:MM-HH:MM" 24-hour range (plain text in SQL)
  //   { kind: 'url' }        — URL
  //   { kind: 'enum', options: [{ value, en, zh }] } — closed list (<select>)
  //   { kind: 'suggest', options: [{ value, en, zh }] } — open list with
  //     suggestions (rendered as <input list>) — schema is open-ended but
  //     common values are surfaced to reduce free-form typos.
  const T = {
    TEXT: { kind: 'text' },
    DECIMAL: { kind: 'number', step: '0.1', min: '0' },
    MONEY: { kind: 'number', step: '1', min: '0' },
    INT0: { kind: 'integer', min: '0' },
    DATE: { kind: 'date' },
    URL: { kind: 'url' },
    // A course's class time as one range. The value is the canonical
    // "HH:MM-HH:MM" string emitted by <TimeRangeField> (components.jsx) and
    // stays a plain text column in SQL — apply_report's existing time_text
    // branch needs NO change for this kind.
    TIMERANGE: { kind: 'timerange' },
    // Canonical per-session schedule, edited via SessionScheduleDialog. The
    // value carried in suggested_value / apply_value is a JSON array string
    // (e.g. '["2026-06-10T14:00","2026-06-17T14:00"]') cast ::jsonb in SQL.
    SCHEDULE: { kind: 'schedule' },
  };
  const ENROLLMENT_STATUS = { kind: 'enum', options: [
    { value: 'Full',     en: 'Full',     zh: '已滿' },
    { value: 'Not full', en: 'Not full', zh: '未滿' },
    { value: 'Closed',   en: 'Closed',   zh: '已結束' },
  ] };
  const TEACHING_MODE = { kind: 'suggest', options: [
    { value: 'Face-to-face', en: 'Face-to-face', zh: '面授' },
    { value: 'Online',       en: 'Online',       zh: '網上' },
    { value: 'Zoom',         en: 'Zoom',         zh: 'Zoom' },
    { value: 'Hybrid',       en: 'Hybrid',       zh: '混合模式' },
  ] };
  const DAY_OF_WEEK = { kind: 'suggest', options: [
    { value: 'Monday',    en: 'Monday',    zh: '星期一' },
    { value: 'Tuesday',   en: 'Tuesday',   zh: '星期二' },
    { value: 'Wednesday', en: 'Wednesday', zh: '星期三' },
    { value: 'Thursday',  en: 'Thursday',  zh: '星期四' },
    { value: 'Friday',    en: 'Friday',    zh: '星期五' },
    { value: 'Saturday',  en: 'Saturday',  zh: '星期六' },
    { value: 'Sunday',    en: 'Sunday',    zh: '星期日' },
  ] };
  const LANGUAGE = { kind: 'suggest', options: [
    { value: 'English',           en: 'English',           zh: '英文' },
    { value: 'Cantonese',         en: 'Cantonese',         zh: '粵語' },
    { value: 'Cantonese/English', en: 'Cantonese/English', zh: '粵語/英文' },
    { value: 'Putonghua',         en: 'Putonghua',         zh: '普通話' },
  ] };
  const EVENT_TYPE = { kind: 'suggest', options: [
    { value: 'Course',                     en: 'Course',                     zh: '課程' },
    { value: 'Conference',                 en: 'Conference',                 zh: '會議' },
    { value: 'Workshop',                   en: 'Workshop',                   zh: '工作坊' },
    { value: 'Seminar',                    en: 'Seminar',                    zh: '研討會' },
    { value: 'AGM + Scientific Meeting',   en: 'AGM + Scientific Meeting',   zh: '周年會員大會 + 學術會議' },
  ] };

  const REPORT_FIELD_GROUPS = [
    {
      label_en: 'Credits & fee', label_zh: '學分與費用',
      items: [
        { key: 'cne_points',         en: 'CNE points',         zh: '學分',           input: T.DECIMAL },
        { key: 'cne_per_session',    en: 'CNE per session',    zh: '每節學分',        input: T.DECIMAL },
        { key: 'fee_hkd',            en: 'Fee (standard)',     zh: '費用（標準）',    input: T.MONEY },
        { key: 'fee_hkd_member',     en: 'Fee (member)',       zh: '費用（會員）',    input: T.MONEY },
        { key: 'fee_hkd_nonmember',  en: 'Fee (non-member)',   zh: '費用（非會員）',  input: T.MONEY },
        { key: 'fee_early_bird_hkd', en: 'Fee (early bird)',   zh: '費用（早鳥）',    input: T.MONEY },
      ],
    },
    {
      label_en: 'Schedule', label_zh: '時間',
      items: [
        { key: 'start_date',            en: 'Start date',            zh: '開始日期', input: T.DATE },
        { key: 'end_date',              en: 'End date',              zh: '結束日期', input: T.DATE },
        { key: 'registration_deadline', en: 'Registration deadline', zh: '報名截止', input: T.DATE },
        { key: 'time_text',             en: 'Class time',            zh: '上課時間', input: T.TIMERANGE },
        { key: 'day_of_week_en',        en: 'Day of week',           zh: '上課日',   input: DAY_OF_WEEK },
        { key: 'sessions',              en: 'Number of sessions',    zh: '節數',     input: T.INT0 },
        // Editing this derives + locks start_date / end_date / sessions too.
        { key: 'sessions_list',         en: 'Full schedule',         zh: '完整課堂時間', input: T.SCHEDULE },
      ],
    },
    {
      label_en: 'Location & format', label_zh: '地點與形式',
      items: [
        { key: 'venue_en',         en: 'Venue (EN)',      zh: '地點（英）',  input: T.TEXT },
        { key: 'venue_zh',         en: 'Venue (中文)',    zh: '地點（中）',  input: T.TEXT },
        { key: 'teaching_mode_en', en: 'Teaching mode',   zh: '授課模式',     input: TEACHING_MODE },
        { key: 'online_platform',  en: 'Online platform', zh: '網上平台',     input: T.TEXT },
        { key: 'language_en',      en: 'Language',        zh: '語言',         input: LANGUAGE },
      ],
    },
    {
      label_en: 'Registration & capacity', label_zh: '報名與名額',
      items: [
        { key: 'registration_url',     en: 'Registration URL',  zh: '報名連結', input: T.URL },
        { key: 'capacity',             en: 'Total capacity',    zh: '容額總數', input: T.INT0 },
        { key: 'remaining_capacity',   en: 'Remaining seats',   zh: '剩餘名額', input: T.INT0 },
        { key: 'enrollment_status_en', en: 'Enrollment status', zh: '報名狀態', input: ENROLLMENT_STATUS },
      ],
    },
    {
      label_en: 'Course info', label_zh: '課程資料',
      items: [
        { key: 'title_en',             en: 'Title (EN)',        zh: '名稱（英）', input: T.TEXT },
        { key: 'title_zh',             en: 'Title (中文)',      zh: '名稱（中）', input: T.TEXT },
        { key: 'course_code',          en: 'Course code',       zh: '課程編號',    input: T.TEXT },
        { key: 'event_type_en',        en: 'Event type',        zh: '活動類型',    input: EVENT_TYPE },
        { key: 'target_audience_en',   en: 'Target audience',   zh: '對象',        input: T.TEXT },
        { key: 'instructors_en',       en: 'Instructors',       zh: '講者',        input: T.TEXT },
        { key: 'description_short_en', en: 'Short description', zh: '簡介',        input: T.TEXT },
      ],
    },
  ];

  // Flat lookup helper for "given a key, find the label / group".
  const REPORT_FIELDS_FLAT = REPORT_FIELD_GROUPS.flatMap((g) => g.items);
  const REPORT_FIELDS_BY_KEY = Object.fromEntries(REPORT_FIELDS_FLAT.map((f) => [f.key, f]));

  // Used by per-field ✎ shortcut: mapping from a course-detail "fact row"
  // to its underlying field_path. Keep keys here aligned with what
  // CourseDetailScreen renders — values must be in REPORT_FIELDS_BY_KEY.
  // If a row's field_path is not in the whitelist, the ✎ should be hidden.
  const PRESET_FIELD_FOR_ROW = {
    points:               'cne_points',
    startDate:            'start_date',
    endDate:              'end_date',
    registrationDeadline: 'registration_deadline',
    venue:                'venue_en',
    registrationUrl:      'registration_url',
    fee:                  'fee_hkd',
    time:                 'time_text',
    schedule:             'sessions_list',
    sessions:             'sessions',
    teachingMode:         'teaching_mode_en',
    language:             'language_en',
    eventType:            'event_type_en',
    targetAudience:       'target_audience_en',
    platform:             'online_platform',
    courseCode:           'course_code',
    enrollmentStatus:     'enrollment_status_en',
    capacity:             'remaining_capacity',
  };

  window.CROWDSOURCE_FIELDS_VERSION = CROWDSOURCE_FIELDS_VERSION;
  window.REPORT_FIELD_GROUPS = REPORT_FIELD_GROUPS;
  window.REPORT_FIELDS_FLAT = REPORT_FIELDS_FLAT;
  window.REPORT_FIELDS_BY_KEY = REPORT_FIELDS_BY_KEY;
  window.PRESET_FIELD_FOR_ROW = PRESET_FIELD_FOR_ROW;
})();
